import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, getDocs, where, limit, doc, getDoc, orderBy } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../hooks/AuthContext';
import { 
  Users, 
  Plus, 
  Search,
  MessageSquare,
  Heart,
  Shield,
  Crown,
  UserPlus,
  Globe,
  Lock,
  TrendingUp,
  Clock,
  Award,
  ChevronRight,
  Home,
  Compass,
  Bell,
  User as UserIcon,
  Filter,
  X,
  Sparkles,
  Flame,
  Star
} from 'lucide-react';
import { Group, PublicProfile } from '../../types/community';
import { Link, useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

export const Community: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [groups, setGroups] = useState<Group[]>([]);
  const [myGroups, setMyGroups] = useState<Group[]>([]);
  const [publicProfiles, setPublicProfiles] = useState<PublicProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'discover' | 'groups' | 'activity' | 'profile'>('discover');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const categories = [
    { id: 'General Support', icon: Users, color: 'from-blue-500 to-blue-600' },
    { id: 'Stress Management', icon: Heart, color: 'from-pink-500 to-rose-500' },
    { id: 'Shift Workers', icon: Clock, color: 'from-purple-500 to-indigo-500' },
    { id: 'Leadership', icon: Crown, color: 'from-yellow-500 to-orange-500' },
    { id: 'Family Support', icon: Shield, color: 'from-green-500 to-emerald-500' },
    { id: 'Peer Support', icon: UserPlus, color: 'from-teal-500 to-cyan-500' }
  ];

  // Fetch community data
  const fetchCommunityData = useCallback(async () => {
    if (!user?.uid) return;

    try {
      setLoading(true);

      // Fetch user's group memberships
      const membershipQuery = query(
        collection(db, 'groupMemberships'),
        where('userId', '==', user.uid),
        limit(50)
      );
      const membershipSnap = await getDocs(membershipQuery);
      const userGroupIds = membershipSnap.docs.map(doc => doc.data().groupId);

      if (userGroupIds.length > 0) {
        const groupsPromises = userGroupIds.map(groupId => 
          getDoc(doc(db, 'groups', groupId))
        );
        const groupsSnaps = await Promise.all(groupsPromises);
        
        const groupsData = groupsSnaps
          .filter(snap => snap.exists())
          .map(snap => ({
            id: snap.id,
            ...snap.data(),
            createdAt: snap.data()?.createdAt?.toDate?.() ?? null,
            updatedAt: snap.data()?.updatedAt?.toDate?.() ?? null,
            lastActivity: snap.data()?.lastActivity?.toDate?.() ?? null,
          } as Group))
          .sort((a, b) => (b.lastActivity?.getTime?.() ?? 0) - (a.lastActivity?.getTime?.() ?? 0));
        
        setGroups(groupsData);
        setMyGroups(groupsData);
      }

      // Fetch public profiles
      const profilesQuery = query(
        collection(db, 'publicProfiles'),
        where('isPublic', '==', true),
        limit(20)
      );
      const profilesSnap = await getDocs(profilesQuery);
      const profilesData = profilesSnap.docs.map(doc => ({
        uid: doc.id,
        ...doc.data(),
        joinedDate: doc.data().joinedDate?.toDate?.() ?? null,
      } as PublicProfile));
      
      setPublicProfiles(profilesData);

    } catch (error) {
      console.error('Error fetching community data:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchCommunityData();
  }, [fetchCommunityData]);

  // Pull to refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchCommunityData();
    setRefreshing(false);
  };

  const filteredGroups = groups.filter(group => {
    const matchesSearch = group.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         group.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || group.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const getCategoryData = (categoryName: string) => 
    categories.find(c => c.id === categoryName) || categories[0];

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse">
            <Users className="w-8 h-8 text-white" />
          </div>
          <p className="text-gray-600 font-medium">Loading Community...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Mobile Header */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-200">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Community</h1>
              <p className="text-xs text-gray-600">{myGroups.length} groups • {publicProfiles.length} members</p>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <TrendingUp className={`w-5 h-5 text-gray-600 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              <Link
                to="/community/create-group"
                className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl hover:shadow-lg transition-all"
              >
                <Plus className="w-5 h-5 text-white" />
              </Link>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search groups..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-20 py-2.5 bg-gray-100 border-0 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
            />
            <button
              onClick={() => setShowFilterSheet(true)}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 px-3 py-1.5 bg-white rounded-lg text-xs font-medium text-gray-700 border border-gray-200 flex items-center space-x-1"
            >
              <Filter className="w-3 h-3" />
              <span>{categoryFilter === 'all' ? 'All' : categoryFilter}</span>
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex items-center px-4 space-x-6 overflow-x-auto scrollbar-hide">
          {[
            { id: 'discover', label: 'Discover', icon: Compass },
            { id: 'groups', label: 'My Groups', icon: Users },
            { id: 'activity', label: 'Activity', icon: Flame },
            { id: 'profile', label: 'Members', icon: UserIcon }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center space-x-2 py-3 border-b-2 transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span className="text-sm font-medium">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-4">
        {/* Discover Tab */}
        {activeTab === 'discover' && (
          <div className="space-y-4">
            {/* Featured Categories */}
            <div className="space-y-3">
              <h2 className="text-sm font-semibold text-gray-900 flex items-center">
                <Sparkles className="w-4 h-4 mr-2 text-yellow-500" />
                Browse by Category
              </h2>
              <div className="grid grid-cols-2 gap-3">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => {
                      setCategoryFilter(category.id);
                      setActiveTab('groups');
                    }}
                    className="group bg-white rounded-2xl p-4 border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all active:scale-95"
                  >
                    <div className={`w-12 h-12 bg-gradient-to-br ${category.color} rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                      <category.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">{category.id}</h3>
                    <p className="text-xs text-gray-600">
                      {groups.filter(g => g.category === category.id).length} groups
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl p-6 text-white">
              <h3 className="text-lg font-bold mb-4">Your Impact</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold">{myGroups.length}</div>
                  <div className="text-xs text-blue-100">Groups</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">
                    {myGroups.reduce((sum, g) => sum + (g.postCount || 0), 0)}
                  </div>
                  <div className="text-xs text-blue-100">Posts</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">
                    {myGroups.reduce((sum, g) => sum + (g.memberCount || 0), 0)}
                  </div>
                  <div className="text-xs text-blue-100">Connections</div>
                </div>
              </div>
            </div>

            {/* Trending Groups */}
            {filteredGroups.length > 0 && (
              <div className="space-y-3">
                <h2 className="text-sm font-semibold text-gray-900 flex items-center">
                  <TrendingUp className="w-4 h-4 mr-2 text-green-500" />
                  Trending Groups
                </h2>
                {filteredGroups.slice(0, 3).map((group) => {
                  const categoryData = getCategoryData(group.category);
                  return (
                    <Link
                      key={group.id}
                      to={`/community/groups/${group.id}`}
                      className="block bg-white rounded-2xl overflow-hidden border border-gray-200 hover:shadow-lg transition-all active:scale-98"
                    >
                      {group.groupImage && (
                        <div className="h-32 w-full bg-gradient-to-br from-gray-100 to-gray-200 relative overflow-hidden">
                          <img
                            src={group.groupImage}
                            alt={group.name}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                          <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                            <div className={`bg-gradient-to-br ${categoryData.color} p-2 rounded-lg`}>
                              <categoryData.icon className="w-4 h-4 text-white" />
                            </div>
                            {group.privacy === 'private' && (
                              <div className="bg-black/40 backdrop-blur-sm px-2 py-1 rounded-lg flex items-center space-x-1">
                                <Lock className="w-3 h-3 text-white" />
                                <span className="text-xs text-white font-medium">Private</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      <div className="p-4">
                        <h3 className="font-semibold text-gray-900 mb-1">{group.name}</h3>
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{group.description}</p>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <div className="flex items-center space-x-3">
                            <div className="flex items-center space-x-1">
                              <Users className="w-3 h-3" />
                              <span>{group.memberCount || 0}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <MessageSquare className="w-3 h-3" />
                              <span>{group.postCount || 0}</span>
                            </div>
                          </div>
                          {group.lastActivity && (
                            <span className="text-gray-400">
                              {formatDistanceToNow(group.lastActivity, { addSuffix: true })}
                            </span>
                          )}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* My Groups Tab */}
        {activeTab === 'groups' && (
          <div className="space-y-3">
            {myGroups.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Groups Yet</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Start connecting with fellow first responders
                </p>
                <Link
                  to="/community/create-group"
                  className="inline-flex items-center justify-center w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-xl font-medium hover:shadow-lg transition-all active:scale-95"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Create Your First Group
                </Link>
              </div>
            ) : (
              filteredGroups.map((group) => {
                const categoryData = getCategoryData(group.category);
                const isLeader = group.leaderId === user?.uid;
                
                return (
                  <Link
                    key={group.id}
                    to={`/community/groups/${group.id}`}
                    className="block bg-white rounded-2xl p-4 border border-gray-200 hover:shadow-lg transition-all active:scale-98"
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`w-12 h-12 bg-gradient-to-br ${categoryData.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                        <categoryData.icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="font-semibold text-gray-900 truncate">{group.name}</h3>
                          {isLeader && (
                            <Crown className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-2 line-clamp-1">{group.description}</p>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <div className="flex items-center space-x-3">
                            <div className="flex items-center space-x-1">
                              <Users className="w-3 h-3" />
                              <span>{group.memberCount || 0}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <MessageSquare className="w-3 h-3" />
                              <span>{group.postCount || 0}</span>
                            </div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </div>
                      </div>
                    </div>
                    {group.lastActivity && (
                      <div className="mt-2 pt-2 border-t border-gray-100 text-xs text-gray-500">
                        Last active {formatDistanceToNow(group.lastActivity, { addSuffix: true })}
                      </div>
                    )}
                  </Link>
                );
              })
            )}
          </div>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <div className="space-y-3">
            <div className="bg-white rounded-2xl p-6 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-100 to-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Flame className="w-8 h-8 text-orange-500" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Activity Feed</h3>
              <p className="text-sm text-gray-600">
                See what's happening in your groups
              </p>
              <p className="text-xs text-gray-500 mt-4">Coming soon...</p>
            </div>
          </div>
        )}

        {/* Members Tab */}
        {activeTab === 'profile' && (
          <div className="space-y-3">
            {publicProfiles.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <UserPlus className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Public Profiles</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Be the first to create a public profile
                </p>
                <Link
                  to="/profile/public"
                  className="inline-flex items-center justify-center w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-xl font-medium hover:shadow-lg transition-all active:scale-95"
                >
                  Create Public Profile
                </Link>
              </div>
            ) : (
              publicProfiles.map((profile) => (
                <div
                  key={profile.uid}
                  className="bg-white rounded-2xl p-4 border border-gray-200"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                      {profile.profilePicture ? (
                        <img
                          src={profile.profilePicture}
                          alt={profile.displayName}
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        <span className="text-white font-bold text-lg">
                          {profile.displayName?.[0]?.toUpperCase() || 'U'}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">
                        {profile.showRealName ? profile.displayName : 'Anonymous Hero'}
                      </h3>
                      <p className="text-sm text-gray-600">{profile.responderType}</p>
                      {profile.showAgency && profile.agency && (
                        <p className="text-xs text-gray-500 mt-1">{profile.agency}</p>
                      )}
                    </div>
                  </div>
                  {profile.badges && profile.badges.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap gap-1">
                      {profile.badges.slice(0, 3).map((badge, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-yellow-100 text-yellow-800"
                        >
                          <Star className="w-3 h-3 mr-1" />
                          {badge}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Filter Bottom Sheet */}
      {showFilterSheet && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowFilterSheet(false)}
          />
          <div className="relative bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-lg sm:m-4 max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-3xl">
              <h3 className="text-lg font-semibold text-gray-900">Filter by Category</h3>
              <button
                onClick={() => setShowFilterSheet(false)}
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="p-6 space-y-2">
              <button
                onClick={() => {
                  setCategoryFilter('all');
                  setShowFilterSheet(false);
                }}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
                  categoryFilter === 'all'
                    ? 'bg-blue-50 text-blue-600 font-medium'
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span>All Categories</span>
                {categoryFilter === 'all' && (
                  <div className="w-2 h-2 bg-blue-600 rounded-full" />
                )}
              </button>
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => {
                    setCategoryFilter(category.id);
                    setShowFilterSheet(false);
                  }}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
                    categoryFilter === category.id
                      ? 'bg-blue-50 text-blue-600 font-medium'
                      : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 bg-gradient-to-br ${category.color} rounded-lg flex items-center justify-center`}>
                      <category.icon className="w-4 h-4 text-white" />
                    </div>
                    <span>{category.id}</span>
                  </div>
                  {categoryFilter === category.id && (
                    <div className="w-2 h-2 bg-blue-600 rounded-full" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-2 z-50">
        <div className="flex items-center justify-around max-w-lg mx-auto">
          {[
            { id: 'discover', label: 'Discover', icon: Compass },
            { id: 'groups', label: 'Groups', icon: Users },
            { id: 'activity', label: 'Activity', icon: Bell },
            { id: 'profile', label: 'Members', icon: UserIcon }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex flex-col items-center space-y-1 py-2 px-3 rounded-xl transition-all ${
                activeTab === tab.id
                  ? 'text-blue-600'
                  : 'text-gray-500'
              }`}
            >
              <tab.icon className={`w-5 h-5 ${activeTab === tab.id ? 'scale-110' : ''}`} />
              <span className="text-xs font-medium">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

